/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  chartLegendCellBar,
  chartLegendCellDashedLine,
  chartLegendRow,
  ChartSpecsWithLegend,
} from '@gh/builder-shared-charts';
import {
  AveragePreconstructionBuildTimesDto,
  AveragePreconstructionBuildTimesPoint,
  AveragePreconstructionBuildTimesValue,
  DashboardChartType,
} from '@gh/builder-shared-data';
import { fill, head } from 'lodash';

export type AveragePreconstructionBuildTimesBudgets = AveragePreconstructionBuildTimesDto;
export const averagePreconstructionBuildTimesFromDto = (dto: AveragePreconstructionBuildTimesDto, isCorporate: boolean):
  ChartSpecsWithLegend => {
  const minBars = 9;
  const emptyValue: AveragePreconstructionBuildTimesValue = {
    xAxisValue: '',
    points: [
      {
        type: DashboardChartType.Build,
        yAxisValue: undefined,
      },
      {
        type: DashboardChartType.Preconstruction,
        yAxisValue: undefined,
      },
    ],
  };
  const dataTable: any[][] = [];
  dataTable.push([
    { type: 'string', label: '' },
  ]);

  const getHoverLabel = (type: DashboardChartType, isCorp: boolean): string => {
    if (isCorp) {
      return DashboardChartType.Build === type ? 'lbl_average_build_time_master_weeks' : 'lbl_average_preconstruction_time_master_weeks';
    }

    return DashboardChartType.Build === type ? 'lbl_average_build_time_franchise_weeks' : 'lbl_average_preconstruction_time_franchise_weeks';
  };

  // Add hidden columns for chart bars width corrections
  if (dto.values.length < minBars) {
    const diff = Math.floor((minBars - dto.values.length) / 2);

    dto.values.unshift(...fill(Array(diff), emptyValue));
    dto.values.push(...fill(Array(diff), emptyValue));
  }

  dto.values.unshift(emptyValue);
  dto.values.push(emptyValue);

  head(dataTable)!.push(...[
    { type: 'number', label: isCorporate ? 'lbl_average_build_time_master' : 'lbl_average_build_time_franchise' },
    { type: 'string', role: 'tooltip', p: { html: true } },
    {
      type: 'number',
      label: isCorporate ? 'lbl_average_preconstruction_time_master' : 'lbl_average_preconstruction_time_franchise',
    },
    { type: 'string', role: 'tooltip', p: { html: true } },
    { type: 'number', label: isCorporate ? 'lbl_average_build_time_region' : 'lbl_average_build_time_master' },
    { type: 'string', role: 'tooltip', p: { html: true } },
    {
      type: 'number',
      label: isCorporate ? 'lbl_average_preconstruction_time_region' : 'lbl_average_preconstruction_time_master',
    },
    { type: 'string', role: 'tooltip', p: { html: true } },
  ]);

  dto.values.forEach((value: AveragePreconstructionBuildTimesValue) => {
    const rowValues: any[] = [value.xAxisValue];
    value.points.forEach((point: AveragePreconstructionBuildTimesPoint) => {
      rowValues.push(...[point.yAxisValue || 0, {
        label: getHoverLabel(point.type, isCorporate),
        value: { count: point.yAxisValue },
      }]);
    });

    rowValues.push(...[
      dto.averageBuildRegion,
      {
        label: isCorporate ? 'lbl_average_build_time_region_weeks' : 'lbl_average_build_time_master_weeks',
        value: { count: dto.averageBuildRegion },
      },
      dto.averagePreconstructionRegion,
      {
        label: isCorporate ? 'lbl_average_preconstruction_time_region_weeks' : 'lbl_average_preconstruction_time_master_weeks',
        value: { count: dto.averagePreconstructionRegion },
      },
    ]);
    dataTable.push(rowValues);
  });

  return {
    legendLayout: [
      chartLegendRow(
        chartLegendCellBar(
          isCorporate ? 'lbl_average_build_time_master' : 'lbl_average_build_time_franchise',
          '#009ce7'),
        chartLegendCellBar(
          isCorporate ? 'lbl_average_preconstruction_time_master' : 'lbl_average_preconstruction_time_franchise',
          '#e500ff')),
      chartLegendRow(
        chartLegendCellDashedLine(
          isCorporate ? 'lbl_average_build_time_region' : 'lbl_average_build_time_master',
          '#00ca00'),
        chartLegendCellDashedLine(
          isCorporate ? 'lbl_average_preconstruction_time_region' : 'lbl_average_preconstruction_time_master',
          '#ff0000')),
    ],
    chartType: 'ComboChart',
    dataTable,
    options: {
      colors: ['#009ce7', '#e500ff'],
      seriesType: 'bars',
      series: {
        2: {
          type: 'line',
          lineDashStyle: [4, 1],
          pointsVisible: false,
          color: '#00ca00',
        },
        3: {
          type: 'line',
          lineDashStyle: [4, 1],
          pointsVisible: false,
          color: '#ff0000',
        },
      },
    },
  };
};
